import {
  useState,
  useEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Box, Grid, Popper, Typography, useMediaQuery } from "@mui/material";
import Button from "../Button";
import Input from "../Input";
import { useTranslation } from "react-i18next";
import TimeScrollWheel from "./TimeScrollWheel";
import TimeWheelDrawer from "./TimeWheelDrawer";

export default function TimeDropdown({
  value,
  onChange,
  InputFieldProps,
  customLabelStyles = true,
  labelShrink,
  renderPickerOnly = false,
  disableInternalOpen = false,
  triggerOnlyProps,
  onOpen,
  onClose,
  ...rest
}: any) {
  const shouldShrink = labelShrink !== undefined ? labelShrink : undefined;
  const isSmallScreen = useMediaQuery("(max-width:899.95px)");
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [selectedMinute, setSelectedMinute] = useState<string | null>(null);
  const [timeValue, setTimeValue] = useState<string>(value || "");

  const InputProps = {
    autoComplete: "off",
    id: "time",
    ...InputFieldProps,
    error: InputFieldProps?.helperText ? InputFieldProps?.error : false,
    customLabelStyles: customLabelStyles,
    inputProps: {
      ...InputFieldProps?.inputProps,
      shrink: timeValue ? true : shouldShrink,
      readOnly: true,
      inputMode: "none" as const,
    },
  };

  const hoursContainerRef = useRef<HTMLDivElement | null>(null);
  const minutesContainerRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const closeDropdown = () => {
    (document.activeElement as HTMLElement | null)?.blur?.();
    setOpen(false);
    onClose?.();
  };

  const hours = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0"),
  );
  const minutes = Array.from({ length: 12 }, (_, i) =>
    (i * 5).toString().padStart(2, "0"),
  );

  const getMobileSelection = () => {
    const sourceValue =
      typeof value === "string" && value.includes(":") ? value : timeValue;

    if (typeof sourceValue === "string" && sourceValue.includes(":")) {
      const [hour = "00", minute = "00"] = sourceValue.split(":");
      return {
        hour: Math.max(0, Math.min(23, parseInt(hour, 10) || 0)),
        minute: Math.max(0, Math.min(55, parseInt(minute, 10) || 0)),
      };
    }

    return { hour: 0, minute: 0 };
  };

  const handleMobileSave = (hour: number, minute: number) => {
    const time = `${hour.toString().padStart(2, "0")}:${minute
      .toString()
      .padStart(2, "0")}`;
    setTimeValue(time);
    if (onChange) onChange(time);
    closeDropdown();
  };

  useEffect(() => {
    if (value) {
      if (typeof value === "string") setTimeValue(value);
    } else {
      setTimeValue("");
    }
  }, [value]);

  // Desktop: auto-confirm when both hour and minute are selected
  useEffect(() => {
    if (isSmallScreen) return;
    if (selectedHour && selectedMinute) {
      const time = `${selectedHour}:${selectedMinute}`;
      setTimeValue(time);
      if (onChange) onChange(time);
      closeDropdown();
    }
  }, [selectedHour, selectedMinute]);

  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const dropdown = document.getElementById("time-dropdown");
    const input =
      document.getElementById("time") ||
      document.getElementById("time_delivery") ||
      document.getElementById("endTime_delivery");

    if (
      dropdown &&
      input &&
      !dropdown.contains(target) &&
      !input.contains(target)
    ) {
      closeDropdown();
    }
  };

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        document.addEventListener("mousedown", handleOutsideClick);
      }, 100);
      return () => {
        clearTimeout(timer);
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      (document.activeElement as HTMLElement | null)?.blur?.();
    };
  }, []);

  useEffect(() => {
    if (value) {
      if (typeof value === "string") {
        setTimeValue(value);
        const [hour, minute] = value.split(":");
        if (hour) setSelectedHour(hour);
        if (minute) setSelectedMinute(minute);
      }
    }
  }, [value]);

  const scrollToMiddle = (container: HTMLDivElement, item: HTMLElement) => {
    const containerHeight = container.clientHeight;
    const itemOffset = item.offsetTop;
    const itemHeight = item.clientHeight;
    const scrollPosition = itemOffset - containerHeight / 2 + itemHeight / 2;
    if (typeof container.scrollTo === "function") {
      try {
        container.scrollTo({ top: scrollPosition, behavior: "smooth" });
      } catch {
        container.scrollTop = scrollPosition;
      }
    } else {
      container.scrollTop = scrollPosition;
    }
  };

  useEffect(() => {
    if (open && !isSmallScreen) {
      const hourToScroll = value ? selectedHour || "12" : "12";
      const minuteToScroll = value ? selectedMinute || "00" : "00";
      const timer = setTimeout(() => {
        const hourElement = document.querySelector(
          `[data-hour="${hourToScroll}"]`,
        ) as HTMLElement | null;
        const minuteElement = document.querySelector(
          `[data-minute="${minuteToScroll}"]`,
        ) as HTMLElement | null;
        if (hourElement && hoursContainerRef.current) {
          scrollToMiddle(hoursContainerRef.current, hourElement);
        }
        if (minuteElement && minutesContainerRef.current) {
          scrollToMiddle(minutesContainerRef.current, minuteElement);
        }
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [open, value, selectedHour, selectedMinute]);

  useEffect(() => {
    if (hoursContainerRef.current) {
      hoursContainerRef.current.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
  }, [hoursContainerRef, open]);

  useEffect(() => {
    if (minutesContainerRef.current) {
      minutesContainerRef.current.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
  }, [minutesContainerRef, open]);

  // Used only for renderPickerOnly (standalone embed outside the drawer)
  const renderMobilePickerContent = () => (
    <Box
      id="time-dropdown"
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
      }}
    >
      <TimeScrollWheel
        initialHour={getMobileSelection().hour}
        initialMinute={getMobileSelection().minute}
        is24h
        onSave={handleMobileSave}
      />
    </Box>
  );

  if (renderPickerOnly) {
    return renderMobilePickerContent();
  }

  return (
    <>
      <Box position="relative" ref={anchorRef}>
        <Input
          value={timeValue || typeof value === "string" ? value : ""}
          {...rest}
          {...InputProps}
          customLabelStyles={customLabelStyles}
          placeholder={t("select_time")}
          onMouseDown={
            triggerOnlyProps?.onMouseDown ||
            ((e: ReactMouseEvent) => e.preventDefault())
          }
          onFocusCapture={triggerOnlyProps?.onFocusCapture}
          onClick={
            triggerOnlyProps?.onClick ||
            (() => {
              if (!disableInternalOpen) {
                setOpen(true);
              }
            })
          }
          onKeyDown={triggerOnlyProps?.onKeyDown}
          readOnly
          InputLabelProps={{
            shrink: shouldShrink !== undefined ? shouldShrink : !!(timeValue || value),
          }}
          label={rest.label || t("time")}
          inputProps={{
            ...InputProps.inputProps,
            ...triggerOnlyProps?.inputProps,
          }}
        />
        {open &&
          (isSmallScreen ? (
            <TimeWheelDrawer
              open={open}
              onClose={closeDropdown}
              is24h
              initialHour={getMobileSelection().hour}
              initialMinute={getMobileSelection().minute}
              onSave={handleMobileSave}
              title={t("time") || "Time"}
              showModeToggle={false}
            />
          ) : (
            <Popper
              open={open && !isSmallScreen}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              modifiers={[
                { name: "offset", options: { offset: [0, 8] } },
              ]}
              style={{ zIndex: 1500 }}
            >
              <Box
                id="time-dropdown"
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0px 4px 16px rgba(0,0,0,0.3)",
                  p: 3,
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography
                      component={"p"}
                      textAlign={"center"}
                      fontSize={16}
                      fontWeight={700}
                    >
                      {t("hours")}
                    </Typography>
                    <Box
                      maxHeight={"270px"}
                      overflow={"auto"}
                      sx={{
                        "&::-webkit-scrollbar": { display: "none" },
                        "-ms-overflow-style": "none",
                        "scrollbar-width": "none",
                      }}
                      mt={1}
                      ref={hoursContainerRef}
                    >
                      {hours.map((hour) => (
                        <div
                          key={hour}
                          ref={hour === selectedHour ? hoursContainerRef : null}
                          data-hour={hour}
                        >
                          <Button
                            fullWidth
                            variant={hour === selectedHour ? "contained" : "text"}
                            onClick={() => setSelectedHour(hour)}
                            sx={{
                              mb: 1,
                              borderRadius: "8px",
                              fontSize: "13px",
                              fontWeight: 700,
                              color: hour === selectedHour ? "#fff" : "#0A0A0E",
                              backgroundColor:
                                hour === selectedHour ? "#520A76" : "transparent",
                              ":hover": {
                                backgroundColor:
                                  hour === selectedHour ? "#520A76" : "#F9F9F9",
                              },
                            }}
                          >
                            {hour}
                          </Button>
                        </div>
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      component={"p"}
                      textAlign={"center"}
                      fontSize={16}
                      fontWeight={700}
                    >
                      {t("min")}
                    </Typography>
                    <Box
                      maxHeight={"270px"}
                      overflow={"auto"}
                      sx={{
                        "&::-webkit-scrollbar": { display: "none" },
                        "-ms-overflow-style": "none",
                        "scrollbar-width": "none",
                      }}
                      mt={1}
                      ref={minutesContainerRef}
                    >
                      {minutes.map((minute) => (
                        <div
                          key={minute}
                          ref={
                            minute === selectedMinute ? minutesContainerRef : null
                          }
                          data-minute={minute}
                        >
                          <Button
                            fullWidth
                            variant={
                              minute === selectedMinute ? "contained" : "text"
                            }
                            sx={{
                              mb: 1,
                              borderRadius: "8px",
                              fontSize: "13px",
                              fontWeight: 700,
                              color:
                                minute === selectedMinute ? "#fff" : "#0A0A0E",
                              backgroundColor:
                                minute === selectedMinute
                                  ? "#520A76"
                                  : "transparent",
                              ":hover": {
                                backgroundColor:
                                  minute === selectedMinute
                                    ? "#520A76"
                                    : "#F9F9F9",
                              },
                            }}
                            onClick={() => setSelectedMinute(minute)}
                          >
                            {minute}
                          </Button>
                        </div>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Popper>
          ))}
      </Box>
    </>
  );
}
